import {getStorage} from "@/store/util.store";
import {useAuthStore} from "../../store/useAuth.store";

import {loadAbort} from "./loadAbort.utility";
import privateInstance from "./private.instance";

const setHeaders = async additionalHeaders => {
  const parsedToken = getStorage("token");
  if (parsedToken) {
    return {
      authorization: `Bearer ${parsedToken}`,
      "Content-Type": "application/json",
      ...additionalHeaders,
    };
  }
  return {
    ...additionalHeaders,
  };
};

const isFormData = body => {
  return typeof FormData !== "undefined" && body instanceof FormData;
};

// Use a function to get the logout method from the store
const getLogout = () => {
  return useAuthStore.getState().logout;
};

const manageCatchError = err => {
  let errorMessage = "";
  let status = null;

  if (err?.response) {
    // Axios error structure
    status = err.response.status;
    errorMessage =
      err.response.data?.message ||
      err.response.data?.error ||
      JSON.stringify(err.response.data) ||
      err.message ||
      "An error occurred";
  } else if (err?.message) {
    // Generic error object
    errorMessage = err.message;
  } else if (typeof err === "string") {
    // Error is a string
    errorMessage = err;
  } else {
    // Unknown error type
    errorMessage = "An unknown error occurred";
  }
  // Check for token validity errors based on the error message
  if (
    errorMessage.includes("Token is not valid") ||
    errorMessage.includes("Invalid Token") ||
    errorMessage.includes("Expired Token")
  ) {
    const logout = getLogout();
    logout();
  }
  // Add more error handling if needed
  if (status === 403 && errorMessage !== "comment_moderated") {
    const logout = getLogout();
    logout();
  }
};

const privateRequest = {
  get: async (url, body, additionalHeaders, transformResponse) => {
    const controller = loadAbort();
    const config = {
      headers: await setHeaders(additionalHeaders),
      signal: controller.signal,
      data: body,
      transformResponse,
    };
    try {
      const response = await privateInstance.get(url, config);
      const {status, data} = response;

      return {data, status};
    } catch (err) {
      manageCatchError(err);
      throw err;
    }
  },

  put: async (url, body, additionalHeaders) => {
    try {
      const config = {
        headers: await setHeaders(additionalHeaders),
      };
      if (isFormData(body)) {
        config.headers["Content-Type"] = "multipart/form-data";
      }
      // send headers and request to URL
      const response = await privateInstance({
        method: "put",
        url,
        data: body,
        ...config,
      });
      const {data, status} = response;
      return {data, status};
    } catch (err) {
      // authentication_required
      manageCatchError(err);
      throw err;
    }
  },
  patch: async (url, body, additionalHeaders) => {
    try {
      const config = {
        headers: await setHeaders(additionalHeaders),
      };
      if (isFormData(body)) {
        config.headers["Content-Type"] = "multipart/form-data";
      }
      // send headers and request to URL
      const response = await privateInstance({
        method: "patch",
        url,
        data: body,
        ...config,
      });
      const {data, status} = response;
      return {data, status};
    } catch (err) {
      // authentication_required
      manageCatchError(err);
      throw err;
    }
  },
  delete: async (url, body, additionalHeaders) => {
    try {
      const config = {
        headers: await setHeaders(additionalHeaders),
      };
      // send headers and request to URL
      const response = await privateInstance({
        method: "delete",
        url,
        data: body,
        ...config,
      });
      const {data, status} = response;
      return {data, status};
    } catch (err) {
      // authentication_required
      manageCatchError(err);
      throw err;
    }
  },
  post: async (url, body, additionalHeaders) => {
    try {
      const config = {
        headers: await setHeaders(additionalHeaders),
      };
      if (isFormData(body)) {
        config.headers["Content-Type"] = "multipart/form-data";
      }
      // send headers and request to URL
      const response = await privateInstance({
        method: "post",
        url,
        data: body,
        ...config,
      });
      const {data, status} = response;
      return {data, status};
    } catch (err) {
      // authentication_required
      manageCatchError(err);
      throw err;
    }
  },
};

export default privateRequest;
